export type BufferedWidgetConfigWrite = {
  key: string
  config: Record<string, string>
  isValid: () => boolean
  isUnchanged: (config: Record<string, string>) => boolean
  write: (config: Record<string, string>) => Promise<void>
}

export class WidgetConfigWriteBuffer {
  private pending = new Map<string, BufferedWidgetConfigWrite>()
  private timer: ReturnType<typeof setTimeout> | null = null
  private active: Promise<void> | null = null
  private retryAfter = 0
  private transientFailures = 0

  constructor(
    private readonly intervalMs: number,
    private readonly now: () => number = Date.now,
    private readonly onChange: () => void = () => {}
  ) {}

  enqueue(next: BufferedWidgetConfigWrite) {
    const current = this.pending.get(next.key)
    // Mount/restore frequently re-emits the saved config. It isn't unsaved user work.
    if (!current && !this.active && (!next.isValid() || next.isUnchanged(next.config))) return
    this.pending.set(next.key, {
      ...next,
      config: {
        ...current?.config,
        ...next.config,
      },
    })
    this.schedule()
    this.onChange()
  }

  hasPending() {
    return this.pending.size > 0 || this.active !== null
  }

  async flushOne() {
    await this.drainOne()
  }

  async flushPendingOnce() {
    let remaining = this.pending.size
    const drainNext = async (): Promise<void> => {
      if (remaining === 0) return
      remaining -= 1
      await this.drainOne()
      await drainNext()
    }
    await drainNext()
  }

  private schedule() {
    if (this.timer !== null || this.pending.size === 0) return

    this.timer = setTimeout(
      () => {
        this.timer = null
        void this.drainOne().finally(() => this.schedule())
      },
      Math.max(this.intervalMs, this.retryAfter - this.now())
    )
  }

  private async drainOne() {
    if (this.now() < this.retryAfter) return
    if (this.active) {
      await this.active
      return
    }

    const entry = this.pending.entries().next().value as
      | [string, BufferedWidgetConfigWrite]
      | undefined
    if (!entry) return

    const [key, task] = entry
    this.pending.delete(key)

    if (!task.isValid() || task.isUnchanged(task.config)) {
      this.onChange()
      return
    }

    this.active = Promise.resolve().then(() => task.write(task.config))
    try {
      await this.active
      this.transientFailures = 0
      this.retryAfter = 0
    } catch (error) {
      const newer = this.pending.get(key)
      this.pending.delete(key)
      this.pending.set(key, {
        ...(newer ?? task),
        config: {
          ...task.config,
          ...newer?.config,
        },
      })
      if ((error as { transient?: boolean })?.transient) {
        this.transientFailures += 1
        this.retryAfter = this.now() + Math.min(30_000 * 2 ** (this.transientFailures - 1), 120_000)
        if (this.transientFailures === 1) {
          console.warn(
            '[WidgetConfigWriteBuffer] Connection unavailable; changes retained for retry'
          )
        }
      } else {
        console.error('[WidgetConfigWriteBuffer] write failed, queued for retry', error)
      }
    } finally {
      this.active = null
      this.onChange()
    }
  }
}
